import { FormGroup, FormSection, TextInput } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils/validation';
import { BittiumRecordingAnalyst } from '@common/enums';
import { translateKey } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { SearchableEnumDropdown } from '../../../../elements/SearchableEnumDropdown';

export interface ISoftware {
    optoConnectPracticeId: string | null;
    sendBittiumRecordingsTo: BittiumRecordingAnalyst | null;
}

export interface ISoftwareProps {
    errorPrefix?: string;
    errors?: ValidationErrors;
    onChange: (key: keyof ISoftware, value: ISoftware[keyof ISoftware]) => void;
    onDoValidate?: (key: keyof ISoftware) => void;
    showOptoConnectPracticeId?: boolean;
    showSendBittiumRecordingsTo?: boolean;
    software: ISoftware;
}

export function Software(props: ISoftwareProps) {
    return (
        <FormSection title={translateKey('serviceOrganizationSetting.software')}>
            {props.showSendBittiumRecordingsTo && (
                <FormGroup
                    hasErrors={hasError('sendBittiumRecordingsTo')}
                    label={translateKey('serviceOrganizationSetting.sendBittiumRecordingsTo')}
                >
                    <SearchableEnumDropdown
                        enumTranslationName="bittiumRecordingAnalyst"
                        error={getError('sendBittiumRecordingsTo')}
                        jsEnum={BittiumRecordingAnalyst}
                        onChange={async (value) => props.onChange('sendBittiumRecordingsTo', value)}
                        onDoValidate={async () => handleValidateOne('sendBittiumRecordingsTo')}
                        placeholder={translateKey('serviceOrganizationSetting.selectSoftware')}
                        value={props.software.sendBittiumRecordingsTo}
                    />
                </FormGroup>
            )}
            {props.showOptoConnectPracticeId && (
                <FormGroup
                    hasErrors={hasError('optoConnectPracticeId')}
                    label={translateKey('serviceOrganizationSetting.optoConnectPracticeId')}
                >
                    <TextInput
                        error={getErrorMessage('optoConnectPracticeId')}
                        onBlur={() => handleValidateOne('optoConnectPracticeId')}
                        onChange={(e) => props.onChange('optoConnectPracticeId', e.target.value ?? null)}
                        placeholder={translateKey('serviceOrganizationSetting.selectOptoConnectPracticeId')}
                        value={props.software.optoConnectPracticeId ?? ''}
                    />
                </FormGroup>
            )}
        </FormSection>
    );

    function hasError(key: keyof ISoftware) {
        const errorKey = getErrorKey(key);

        return props.errors?.has(errorKey);
    }

    function getError(key: keyof ISoftware) {
        const errorKey = getErrorKey(key);

        return props.errors?.get(errorKey);
    }

    function getErrorMessage(key: keyof ISoftware) {
        const errorKey = getErrorKey(key);

        return getValidationErrorTranslation(props.errors?.get(errorKey));
    }

    function getErrorKey(key: keyof ISoftware) {
        return `${props.errorPrefix ?? ''}${key}`;
    }

    function handleValidateOne(key: keyof ISoftware) {
        if (props.onDoValidate) {
            props.onDoValidate(key);
        }
    }
}
